import { nextUnlock, UNLOCKS, type UnlockDef } from '../core/game/unlock-rules.js';
import { el } from './dom.js';
import { menuShell } from './menu-shell.js';
import { prizeArt } from '../content/prize-art.js';
import type { Screen } from './router.js';

/**
 * Everything she has earned, and everything still to earn.
 *
 * **A shelf to look at.** It used to have a second job - tapping a hat put it on the pup - and that job
 * went with the hats: the traced show pups arrive wearing what identifies them, so nothing the shelf could
 * offer to wear was ever visible in the cab. What is left is the job that made it worth building: prizes
 * she is given and then never sees again are prizes she stops caring about, and a shelf she can visit is
 * what makes the next one worth driving for. Cars and stickers need no wearing - a car turns up in the
 * chooser, a sticker in her tray - so there is nothing here to press, and no press that does nothing.
 *
 * Locked items are shown as dimmed silhouettes rather than hidden. Seeing that there *is* something else
 * is the entire point of a collection, and an empty grid that fills up invisibly teaches nothing - it is
 * also the only place a car she has not earned is visible at all, since a locked car is absent from the
 * chooser rather than greyed out in it. There are no numbers anywhere: she cannot read a treat count, and
 * the next thing along is always the next thing along.
 */

export interface CollectionScreenOptions {
  /** Injected for tests, and so a missing image costs the picture rather than the screen. */
  readonly loadPrizeArt?: (unlock: UnlockDef) => Promise<HTMLElement | null>;
}

/** Six a page, the same shelf shape as the Garage - see `src/shell/menu-shell.ts`. */
const PER_PAGE = 6;

export function createCollectionScreen(options: CollectionScreenOptions = {}): Screen {
  const loadPrizeArt = options.loadPrizeArt ?? prizeArt;

  let disposed = false;

  return {
    mount(root, ctx) {
      const owned = new Set(ctx.currentSave().unlocks);
      const upcoming = nextUnlock(ctx.currentSave().pawPoints, ctx.currentSave().unlocks);

      /*
       * Paged rather than scrolled, six to a page, in unlock order - cheapest first, so the shelf reads
       * left to right as the order she earned them. Paging stays now that the ladder is seven prizes rather
       * than twenty: a grid that scrolls moves under her finger, and every button in it fires on
       * pointerdown.
       */
      const shell = menuShell({
        screenClass: 'screen--collection',
        badge: 'prizes',
        badgeTitle: 'prizes',
        onBack: () => ctx.dispatch({ type: 'BACK' }),
      });

      shell.paginate(
        UNLOCKS.map((unlock) =>
          card(unlock, { owned: owned.has(unlock.id), next: upcoming?.id === unlock.id }),
        ),
        PER_PAGE,
      );

      root.append(shell.screen);
    },

    unmount() {
      disposed = true;
    },
  };

  function card(unlock: UnlockDef, state: { owned: boolean; next: boolean }): HTMLElement {
    const { owned: isOwned, next: isNext } = state;
    const art = el('div', { className: 'collection-art' });
    const host = el('div', {
      className: ['collection-card', 'card', isOwned ? 'is-owned' : 'is-locked', isNext ? 'is-next' : '']
        .filter(Boolean)
        .join(' '),
      children: [art],
    });
    host.title = unlock.label;
    // Named, so a card can be found wherever it is. With paging a card is not always in the document, let
    // alone at its own index in the grid.
    host.dataset['unlock'] = unlock.id;

    fillArt(unlock, art);

    return host;
  }

  /**
   * Each prize shows the thing itself - the car's own outline, or the set's own stickers.
   *
   * There used to be a branch here for prizes that were not hats, a signpost for a route and a horn glyph
   * for a horn, and it went with them: on a shelf whose entire job is "look what there is to earn", a card
   * standing in for something she can never see was the weakest card on it. Every prize is now a thing with
   * a picture of its own, and choosing which picture is `src/content/prize-art.ts`'s one job.
   */
  function fillArt(unlock: UnlockDef, host: HTMLElement): void {
    void loadPrizeArt(unlock).then((art) => {
      if (disposed || art === null) return;
      host.replaceChildren(art);
    });
  }
}
